import React, { useEffect, useState } from 'react';
import { Layout, Typography, Button, Space, Table, Input, Tag, Row, Col, Select, Card, Alert, Collapse, Divider, Tooltip, Tabs } from 'antd';
import {
    LeftOutlined,
    ReloadOutlined,
    UserOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    ClockCircleOutlined,
    SearchOutlined,
    InfoCircleOutlined
} from '@ant-design/icons';
import { withRouter } from 'react-router-dom';
import useCampaignsStore from '../../stores/useCampaignsStore';
import PhoneDisplay from '../common/PhoneDisplay';
import UserAvatar from '../common/UserAvatar';
import { getAllGroups } from '../../reducers/campaign/resources';
import templateResources from '../../reducers/templates/resources';
import { evaluateTCPA } from '../../utils/tcpa';
import { formatInTimezone, formatInUserTime, formatSchedule } from '../../utils/campaign-timezone';
import CampaignFlowSection from './shared/CampaignFlowSection';
import CampaignSchedulingDetails from './shared/CampaignSchedulingDetails';
import FlowEnrollmentBoard from '../flow-enrollment/FlowEnrollmentBoard';
import CampaignContactDrawer from './CampaignContactDrawer';
import useFlowEnrollmentStore from '../../stores/useFlowEnrollmentStore';
import useModuleGroupStore from '../../stores/useModuleGroupStore';
import constants from "../../constants";

const { Content } = Layout;
const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

const compactStyles = {
    page: {
        padding: '8px 12px 12px',
        background: 'var(--gh-gray-50, #FAFAFA)'
    },
    shell: {
        display: 'flex',
        flexDirection: 'column',
        gap: 6
    },
    panel: {
        background: '#fff',
        border: '1px solid var(--gh-gray-200, #E6E6E6)',
        borderRadius: 8
    },
    header: {
        padding: '8px 10px'
    },
    subText: {
        fontSize: 12,
        color: 'var(--gh-text-secondary, #607D8B)'
    },
    detailLabel: {
        fontSize: 12,
        color: 'var(--gh-text-secondary, #607D8B)',
        display: 'block',
        marginBottom: 2
    },
    detailValue: {
        fontSize: 13,
        color: 'var(--gh-text-primary, #263238)',
        fontWeight: 500,
        minHeight: 20
    },
    controlsRow: {
        display: 'flex',
        gap: 8,
        alignItems: 'center',
        marginTop: 6,
        marginBottom: 6
    }
};

const getDetailedMessageStatus = (record, campaignStatus) => {
    if (!campaignStatus) return 'Not Sent';

    switch (record.message_status) {
        case 'delivered':
            return 'Confirmed';
        case 'accepted':
        case 'sent':
            return 'Sent';
        case 'expired':
            return 'Failed (Expired)';
        case 'undelivered':
            return 'Failed (Undelivered)';
        case 'failed':
            return String(record.provider_code || '') === '6' ? 'Failed (Spam)' : 'Failed (Unknown)';
        case null:
        case undefined:
            if (record.opted_out_date || String(record.agreement || '').toLowerCase() === 'no') {
                return 'Not Sent (Contact Opted Out)';
            }
            if (record.provider_id) {
                return 'Sent';
            }
            return 'Failed (Unknown)';
        default:
            return 'Failed (Unknown)';
    }
};

const getMessageTagTone = (label) => {
    if (label === 'Confirmed' || label === 'Sent') {
        return {
            color: 'var(--gh-primary-active, #2E7D32)',
            background: 'var(--gh-primary-tint, #E8F5E9)',
            border: 'var(--gh-primary-border, #A5D6A7)'
        };
    }

    if (label.startsWith('Not Sent')) {
        return {
            color: 'var(--gh-warning, #FFB300)',
            background: 'rgba(255,179,0,0.12)',
            border: 'rgba(255,179,0,0.28)'
        };
    }

    if (label.startsWith('Failed')) {
        return {
            color: 'var(--gh-danger, #FF4D4F)',
            background: 'rgba(255,77,79,0.12)',
            border: 'rgba(255,77,79,0.28)'
        };
    }

    return {
        color: 'var(--gh-text-secondary, #607D8B)',
        background: 'var(--gh-gray-100, #F0F0F0)',
        border: 'var(--gh-gray-200, #E6E6E6)'
    };
};

const getTcpaStatus = (record) => {
    const optedInDate = record.opted_in_date || '';
    const optedOutDate = record.opted_out_date || '';
    const agreement = (record.agreement || '').toString();

    if (!optedInDate && !optedOutDate && !agreement) {
        return '--';
    }

    const tcpa = evaluateTCPA({
        optedInDate,
        optedOutDate,
        agreement
    });

    return tcpa === 0 ? 'Opted In' : 'Opted Out';
};

const labelFromLookup = (map, id) => {
    if (!id) return '--';
    return map[id] || id;
};

const DetailField = ({ label, value }) => (
    <div style={{ minHeight: 42 }}>
        <span style={compactStyles.detailLabel}>{label}</span>
        <div style={compactStyles.detailValue}>{value || '--'}</div>
    </div>
);

const CampaignViewV2 = ({ match, history }) => {
    const { campaignId } = match.params;
    const {
        contactsLoading,
        campaignDetails,
        campaignContacts,
        contactsPagination,
        contactsFilters,
        setContactsFilters,
        setContactsPage,
        fetchCampaignDetails,
        fetchCampaignContacts
    } = useCampaignsStore();

    const [lookupMaps, setLookupMaps] = useState({ groupsById: {}, templatesById: {} });
    const [drawerContactId, setDrawerContactId] = useState(null);
    const [drawerEnrollment, setDrawerEnrollment] = useState(null);

    const { boardData } = useFlowEnrollmentStore();
    const { modules } = useModuleGroupStore();

    const boardKey = campaignDetails?.modulegroup_id
        ? (campaignId ? `${campaignDetails.modulegroup_id}:${campaignId}` : campaignDetails.modulegroup_id)
        : null;
    const enrollments = boardKey ? (boardData[boardKey] || []) : [];

    // Sync drawer enrollment from store when board data changes (keeps drawer reactive after move/drop)
    useEffect(() => {
        if (!drawerEnrollment?.id) return;
        const fresh = enrollments.find((e) => e.id === drawerEnrollment.id);
        if (fresh && (fresh.current_module_id !== drawerEnrollment.current_module_id || fresh.status !== drawerEnrollment.status)) {
            setDrawerEnrollment(fresh);
        }
    }, [enrollments]);

    const handleContactRowClick = (record) => {
        const enrollment = enrollments.find((e) => e.contact_id === record.contact_id);
        setDrawerEnrollment(enrollment || null);
        setDrawerContactId(record.contact_id);
    };

    const handleEnrollmentCardClick = (enrollment) => {
        setDrawerEnrollment(enrollment);
        setDrawerContactId(enrollment.contact_id);
    };

    const handleDrawerClose = () => {
        setDrawerContactId(null);
        setDrawerEnrollment(null);
    };

    useEffect(() => {
        if (campaignId) {
            fetchCampaignDetails(campaignId);
            fetchCampaignContacts(campaignId);
        }
    }, [campaignId]);

    useEffect(() => {
        let mounted = true;

        const loadLookups = async () => {
            try {
                const [groupsRes, templatesRes] = await Promise.all([
                    getAllGroups(),
                    templateResources.getTemplates()
                ]);

                if (!mounted) return;

                const groupsById = (groupsRes?.data || []).reduce((acc, group) => {
                    acc[group.id] = group.name;
                    return acc;
                }, {});

                const templatesById = (templatesRes?.data || []).reduce((acc, template) => {
                    acc[template.id] = template.name;
                    return acc;
                }, {});

                setLookupMaps({ groupsById, templatesById });
            } catch (error) {
                // Lookup data is optional for display labels.
                setLookupMaps({ groupsById: {}, templatesById: {} });
            }
        };

        loadLookups();

        return () => {
            mounted = false;
        };
    }, []);

    const [localSearch, setLocalSearch] = React.useState(contactsFilters.search);

    useEffect(() => {
        setLocalSearch(contactsFilters.search);
    }, [contactsFilters.search]);

    useEffect(() => {
        const timer = setTimeout(() => {
            if (localSearch !== contactsFilters.search) {
                setContactsFilters({ search: localSearch });
            }
        }, 400);

        return () => clearTimeout(timer);
    }, [localSearch]);

    const handleTableChange = (pag, filters, sorter) => {
        if (pag.current !== contactsPagination.page || pag.pageSize !== contactsPagination.pageSize) {
            setContactsPage(pag.current, pag.pageSize);
        }

        if (sorter.field && sorter.order) {
            const order = sorter.order === 'ascend' ? 'asc' : 'desc';
            if (sorter.field !== contactsFilters.sort || order !== contactsFilters.order) {
                setContactsFilters({ sort: sorter.field, order });
            }
        }
    };

    const columns = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            sorter: true,
            width: 260,
            ellipsis: true,
            render: (text, record) => (
                <a
                    className="gh-table-link"
                    onClick={(e) => { e.stopPropagation(); handleContactRowClick(record); }}
                    title={text}
                    style={{
                        cursor: 'pointer',
                        display: 'inline-block',
                        maxWidth: '100%',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        verticalAlign: 'middle'
                    }}
                >
                    {text}
                </a>
            )
        },
        {
            title: 'Mobile',
            dataIndex: 'mobile',
            key: 'mobile',
            sorter: true,
            render: (text) => <PhoneDisplay value={text} />
        },
        {
            title: 'TCPA Status',
            key: 'tcpa',
            render: (_, record) => {
                const tcpa = getTcpaStatus(record);
                if (tcpa === '--') return '--';
                return (
                    <Tag style={{ borderRadius: 999, marginRight: 0 }}>
                        {tcpa}
                    </Tag>
                );
            }
        },
        {
            title: 'Message Status',
            dataIndex: 'message_status',
            key: 'status',
            sorter: true,
            render: (_, record) => {
                const label = getDetailedMessageStatus(record, campaignDetails?.status);
                const tone = getMessageTagTone(label);
                return (
                    <Tag
                        style={{
                            color: tone.color,
                            background: tone.background,
                            borderColor: tone.border,
                            borderRadius: 999,
                            fontSize: 11,
                            lineHeight: '18px',
                            paddingInline: 8,
                            marginRight: 0
                        }}
                    >
                        {label}
                    </Tag>
                );
            }
        }
    ];

    if (!campaignDetails) {
        return <div style={{ padding: 20, textAlign: 'center' }}>Loading...</div>;
    }

    if (!campaignDetails) {
        return <div style={{ padding: 20, textAlign: 'center' }}>Campaign not found</div>;
    }

    const { uiStatus = {}, stats = {} } = campaignDetails;
    const delivered = (stats.accepted || 0) + (stats.delivered || 0);
    const failed = (stats.failed || 0) + (stats.expired || 0);
    const pending = Math.max((stats.total || 0) - (delivered + failed), 0);
    const reviewStatus = campaignDetails.is_active ? 'Approved' : 'Pending';
    const isPrivateCampaign = campaignDetails.privacy === 'private';
    const usersToNotify = campaignDetails.users_to_notify || [];
    const messageBody = campaignDetails.message || '--';
    const createdBy = campaignDetails.creator_name || campaignDetails.username || campaignDetails.created_by || '--';
    const creatorTimezone = campaignDetails.creator_timezone || '';
    const createdAtLabel = formatInTimezone(campaignDetails.created_at, creatorTimezone);

    return (
        <Layout style={compactStyles.page}>
            <Content style={compactStyles.shell}>
                <div style={{ ...compactStyles.panel, ...compactStyles.header }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                        <Button
                            size="small"
                            type="text"
                            icon={<LeftOutlined />}
                            onClick={() => history.push('/campaign')}
                        />

                        <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                                <Title
                                    level={5}
                                    style={{
                                        margin: 0,
                                        color: 'var(--gh-text-primary, #263238)',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    {campaignDetails.name}
                                </Title>
                                <Tag color={uiStatus.color} style={{ marginRight: 0, borderRadius: 999 }}>
                                    {uiStatus.status || 'Unknown'}
                                </Tag>
                            </div>
                            <Text style={compactStyles.subText}>
                                <Tooltip title={formatInUserTime(campaignDetails.created_at)}>
                                    <span>
                                        Created {createdAtLabel}{creatorTimezone ? ` (${creatorTimezone})` : ''}
                                    </span>
                                </Tooltip>
                            </Text>
                        </div>

                        <Button
                            size="small"
                            icon={<ReloadOutlined />}
                            onClick={() => {
                                fetchCampaignDetails(campaignId);
                                fetchCampaignContacts(campaignId);
                            }}
                        >
                            Refresh
                        </Button>
                    </div>

                    {!campaignDetails.hasCampaignBot && (
                        <Alert
                            type="warning"
                            showIcon
                            style={{ marginBottom: 8 }}
                            message="No Campaign Bot"
                            description="This campaign has no broadcast campaign bot configured."
                        />
                    )}

                    <Row gutter={[6, 6]} style={{ marginBottom: 6 }}>
                        <Col xs={12} sm={6}>
                            <Card size="small" bodyStyle={{ padding: '6px 8px' }} style={{ borderRadius: 8 }}>
                                <Space size={8}>
                                    <UserOutlined style={{ color: 'var(--gh-text-secondary, #607D8B)' }} />
                                    <div>
                                        <div style={compactStyles.subText}>Contacts</div>
                                        <div style={{ ...compactStyles.detailValue, fontSize: 18 }}>{stats.total || 0}</div>
                                    </div>
                                </Space>
                            </Card>
                        </Col>
                        <Col xs={12} sm={6}>
                            <Card size="small" bodyStyle={{ padding: '6px 8px' }} style={{ borderRadius: 8, background: 'var(--gh-primary-tint, #E8F5E9)' }}>
                                <Space size={8}>
                                    <CheckCircleOutlined style={{ color: 'var(--gh-primary-active, #2E7D32)' }} />
                                    <div>
                                        <div style={compactStyles.subText}>Msg. Delivered</div>
                                        <div style={{ ...compactStyles.detailValue, fontSize: 18, color: 'var(--gh-primary-active, #2E7D32)' }}>{delivered}</div>
                                    </div>
                                </Space>
                            </Card>
                        </Col>
                        <Col xs={12} sm={6}>
                            <Card size="small" bodyStyle={{ padding: '6px 8px' }} style={{ borderRadius: 8, background: 'rgba(255,77,79,0.08)' }}>
                                <Space size={8}>
                                    <CloseCircleOutlined style={{ color: 'var(--gh-danger, #FF4D4F)' }} />
                                    <div>
                                        <div style={compactStyles.subText}>Msg. Failed</div>
                                        <div style={{ ...compactStyles.detailValue, fontSize: 18, color: 'var(--gh-danger, #FF4D4F)' }}>{failed}</div>
                                    </div>
                                </Space>
                            </Card>
                        </Col>
                        <Col xs={12} sm={6}>
                            <Card size="small" bodyStyle={{ padding: '6px 8px' }} style={{ borderRadius: 8 }}>
                                <Space size={8}>
                                    <ClockCircleOutlined style={{ color: 'var(--gh-warning, #FFB300)' }} />
                                    <div>
                                        <div style={compactStyles.subText}>Pending</div>
                                        <div style={{ ...compactStyles.detailValue, fontSize: 18, color: 'var(--gh-warning, #FFB300)' }}>{pending}</div>
                                    </div>
                                </Space>
                            </Card>
                        </Col>
                    </Row>
                </div>

                <div style={{ ...compactStyles.panel }}>
                    <Collapse
                        defaultActiveKey={[]}
                        ghost
                        items={[{
                            key: 'details',
                            label: (
                                <span style={{ fontWeight: 500, fontSize: 13 }}>
                                    <InfoCircleOutlined style={{ marginRight: 6 }} />
                                    Campaign Details
                                </span>
                            ),
                            children: (
                                <>
                                    <Row gutter={[12, 8]}>
                                        <Col xs={24} sm={12} md={6}>
                                            <DetailField label="Campaign Name" value={campaignDetails.name} />
                                        </Col>
                                        <Col xs={24} sm={12} md={6}>
                                            <DetailField label="Review Status" value={reviewStatus} />
                                        </Col>
                                        <Col xs={24} sm={12} md={6}>
                                            <span style={compactStyles.detailLabel}>Created By</span>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 22 }}>
                                                <UserAvatar
                                                    name={createdBy === '--' ? 'Unknown' : createdBy}
                                                    userId={campaignDetails.user_id}
                                                    size={22}
                                                    showTooltip
                                                />
                                                <span style={{ ...compactStyles.detailValue, minHeight: 0, fontWeight: 400 }}>
                                                    {createdBy}
                                                </span>
                                            </div>
                                        </Col>
                                        <Col xs={24} sm={12} md={6}>
                                            <DetailField label="Group" value={labelFromLookup(lookupMaps.groupsById, campaignDetails.group_id)} />
                                        </Col>
                                        <Col xs={24} sm={12} md={6}>
                                            <DetailField
                                                label="No. of Contacts"
                                                value={`${stats.total || 0} contacts${stats.nontcpa ? ` (${stats.nontcpa} opted out)` : ''}`}
                                            />
                                        </Col>
                                        <Col xs={24} sm={12} md={6}>
                                            <DetailField
                                                label="Schedule"
                                                value={(
                                                    <Tooltip title={formatInUserTime(campaignDetails.schedule)}>
                                                        <span>{formatSchedule(campaignDetails.schedule, campaignDetails.timezone)}</span>
                                                    </Tooltip>
                                                )}
                                            />
                                        </Col>
                                        <Col xs={24} sm={12} md={6}>
                                            <DetailField label="Schedule Timezone" value={campaignDetails.timezone || '--'} />
                                        </Col>
                                        <Col xs={24} sm={12} md={6}>
                                            <DetailField label="Template" value={labelFromLookup(lookupMaps.templatesById, campaignDetails.template_id)} />
                                        </Col>
                                        <Col xs={24} sm={12} md={6}>
                                            <DetailField label="Campaign Status" value={uiStatus.status || '--'} />
                                        </Col>
                                    </Row>

                                    <Divider style={{ margin: '8px 0' }} />

                                    <Row gutter={[12, 6]}>
                                        <Col xs={24} md={8}>
                                            <DetailField label="Private Campaign" value={isPrivateCampaign ? 'Yes' : 'No'} />
                                        </Col>
                                        <Col xs={24} md={16}>
                                            <span style={compactStyles.detailLabel}>Users to notify of responses</span>
                                            {usersToNotify.length ? (
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                                    {usersToNotify.map(user => (
                                                        <div key={user.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                            <UserAvatar
                                                                name={user.name || 'Unknown'}
                                                                userId={user.id}
                                                                size={22}
                                                                showTooltip
                                                            />
                                                            <span style={{ ...compactStyles.detailValue, minHeight: 0, fontWeight: 400 }}>
                                                                {user.name || 'Unknown'}{user.email ? ` - ${user.email}` : ''}
                                                            </span>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <div style={compactStyles.detailValue}>--</div>
                                            )}
                                        </Col>
                                    </Row>

                                    {!campaignDetails.modulegroup_id && (
                                        <>
                                            <Divider style={{ margin: '8px 0' }} />

                                            <Paragraph style={{ marginBottom: 4 }}>
                                                <Text strong>Message</Text>
                                                <Text type="secondary" style={{ float: 'right' }}>{`${(campaignDetails.message || '').length}/${constants.maxMessageLength}`}</Text>
                                            </Paragraph>
                                            <div
                                                style={{
                                                    border: '1px solid var(--gh-gray-200, #E6E6E6)',
                                                    background: 'var(--gh-gray-50, #FAFAFA)',
                                                    borderRadius: 8,
                                                    padding: '8px 10px',
                                                    minHeight: 56,
                                                    whiteSpace: 'pre-wrap'
                                                }}
                                            >
                                                {messageBody}
                                            </div>

                                            {campaignDetails.no_slots_message ? (
                                                <>
                                                    <Paragraph style={{ margin: '8px 0 4px' }}>
                                                        <Text strong>Message shown when no interview slots are available</Text>
                                                    </Paragraph>
                                                    <div
                                                        style={{
                                                            border: '1px solid var(--gh-gray-200, #E6E6E6)',
                                                            background: 'var(--gh-gray-50, #FAFAFA)',
                                                            borderRadius: 8,
                                                            padding: '8px 10px',
                                                            minHeight: 44,
                                                            whiteSpace: 'pre-wrap'
                                                        }}
                                                    >
                                                        {campaignDetails.no_slots_message}
                                                    </div>
                                                </>
                                            ) : null}
                                        </>
                                    )}
                                </>
                            )
                        }]}
                    />
                </div>

                {campaignDetails.modulegroup_id && (
                    <CampaignFlowSection
                        moduleGroupId={campaignDetails.modulegroup_id}
                        campaignState="sent"
                        defaultExpanded={false}
                        flowSnapshot={campaignDetails.flow_snapshot}
                        moduleResponseOverrides={campaignDetails.module_response_overrides}
                        noSlotsMessage={campaignDetails.no_slots_message}
                    />
                )}

                <CampaignSchedulingDetails
                    availabilitySettings={campaignDetails.availability_settings}
                    flowSnapshot={campaignDetails.flow_snapshot}
                    defaultExpanded={false}
                    schedulingUserId={campaignDetails.scheduling_user_id}
                    schedulingUserIds={campaignDetails.scheduling_user_ids}
                    schedulingStrategy={campaignDetails.scheduling_strategy}
                    schedulingUsers={campaignDetails.scheduling_users}
                    creatorName={createdBy}
                />

                <div style={{ ...compactStyles.panel, padding: '0 10px 8px' }}>
                    {campaignDetails.modulegroup_id ? (
                        <Tabs
                            size="small"
                            style={{ padding: '0 4px' }}
                            items={[
                                {
                                    key: 'contacts',
                                    label: `Contacts (${contactsPagination.total || 0})`,
                                    children: (
                                        <>
                                            <div style={compactStyles.controlsRow}>
                                                <Input
                                                    placeholder="Search contacts..."
                                                    style={{ width: 260 }}
                                                    value={localSearch}
                                                    onChange={e => setLocalSearch(e.target.value)}
                                                    allowClear
                                                    size="small"
                                                    prefix={<SearchOutlined style={{ color: 'var(--gh-text-secondary, #607D8B)' }} />}
                                                />
                                                <Select
                                                    value={contactsFilters.status}
                                                    style={{ width: 160 }}
                                                    onChange={val => setContactsFilters({ status: val })}
                                                    size="small"
                                                >
                                                    <Option value="all">All Status</Option>
                                                    <Option value="sent">Sent/Delivered</Option>
                                                    <Option value="delivered">Delivered</Option>
                                                    <Option value="failed">Failed</Option>
                                                </Select>
                                            </div>
                                            <Table
                                                className="gh-themed-table"
                                                columns={columns}
                                                dataSource={campaignContacts}
                                                rowKey="contact_id"
                                                loading={contactsLoading}
                                                size="small"
                                                onRow={(record) => ({
                                                    onClick: () => handleContactRowClick(record),
                                                    style: { cursor: 'pointer' },
                                                })}
                                                pagination={{
                                                    current: contactsPagination.page,
                                                    pageSize: contactsPagination.pageSize,
                                                    total: contactsPagination.total,
                                                    showSizeChanger: true,
                                                    size: 'small',
                                                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`
                                                }}
                                                onChange={handleTableChange}
                                            />
                                        </>
                                    )
                                },
                                {
                                    key: 'enrollment',
                                    label: 'Enrollment Progress',
                                    children: (
                                        <FlowEnrollmentBoard
                                            modulegroupId={campaignDetails.modulegroup_id}
                                            campaignId={campaignId}
                                            flowSnapshot={campaignDetails.flow_snapshot}
                                            moduleResponseOverrides={campaignDetails.module_response_overrides}
                                            onCardClick={handleEnrollmentCardClick}
                                        />
                                    )
                                }
                            ]}
                        />
                    ) : (
                        <>
                            <div style={compactStyles.controlsRow}>
                                <Input
                                    placeholder="Search contacts..."
                                    style={{ width: 260 }}
                                    value={localSearch}
                                    onChange={e => setLocalSearch(e.target.value)}
                                    allowClear
                                    size="small"
                                    prefix={<SearchOutlined style={{ color: 'var(--gh-text-secondary, #607D8B)' }} />}
                                />
                                <Select
                                    value={contactsFilters.status}
                                    style={{ width: 160 }}
                                    onChange={val => setContactsFilters({ status: val })}
                                    size="small"
                                >
                                    <Option value="all">All Status</Option>
                                    <Option value="sent">Sent/Delivered</Option>
                                    <Option value="delivered">Delivered</Option>
                                    <Option value="failed">Failed</Option>
                                </Select>
                            </div>
                            <Table
                                className="gh-themed-table"
                                columns={columns}
                                dataSource={campaignContacts}
                                rowKey="contact_id"
                                loading={contactsLoading}
                                size="small"
                                onRow={(record) => ({
                                    onClick: () => handleContactRowClick(record),
                                    style: { cursor: 'pointer' },
                                })}
                                pagination={{
                                    current: contactsPagination.page,
                                    pageSize: contactsPagination.pageSize,
                                    total: contactsPagination.total,
                                    showSizeChanger: true,
                                    size: 'small',
                                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`
                                }}
                                onChange={handleTableChange}
                            />
                        </>
                    )}
                </div>

                <CampaignContactDrawer
                    open={!!drawerContactId}
                    onClose={handleDrawerClose}
                    contactId={drawerContactId}
                    enrollment={drawerEnrollment}
                    modulegroupId={campaignDetails.modulegroup_id}
                    campaignId={campaignId}
                    modules={modules}
                    flowSnapshot={campaignDetails.flow_snapshot}
                    moduleResponseOverrides={campaignDetails.module_response_overrides}
                />
            </Content>
        </Layout>
    );
};

export default withRouter(CampaignViewV2);
